import React, { useState } from 'react';
import { useScreenCapture } from '../hooks/useScreenCapture';
import Button from './Button';

const RecordingDebug: React.FC = () => {
  const { isRecording, isSupported, error, startRecording, stopRecording } = useScreenCapture();
  const [logs, setLogs] = useState<string[]>([]);

  const addLog = (message: string) => {
    setLogs(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const handleStartRecording = async () => {
    addLog('Attempting to start recording...');
    try {
      await startRecording({
        width: 1920,
        height: 1080,
        frameRate: 30,
        quality: 2.5
      });
      addLog('Recording started successfully');
    } catch (err) {
      addLog(`Recording failed: ${err}`);
    }
  };

  const handleStopRecording = () => {
    addLog('Stopping recording...');
    stopRecording();
    addLog('Recording stopped');
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Recording Debug Panel</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Status Panel */}
        <div className="card">
          <div className="card-content">
            <h2 className="text-lg font-semibold mb-4">Status</h2>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Supported:</span>
                <span className={isSupported ? 'text-green-600' : 'text-red-600'}>
                  {isSupported ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Recording:</span>
                <span className={isRecording ? 'text-red-600' : 'text-gray-600'}>
                  {isRecording ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Error:</span>
                <span className={error ? 'text-red-600' : 'text-green-600'}>
                  {error || 'None'}
                </span>
              </div>
            </div>
            
            <div className="mt-4 space-x-2">
              <Button
                variant="primary"
                size="md"
                onClick={handleStartRecording}
                disabled={!isSupported || isRecording}
              >
                Start Recording
              </Button>
              <Button
                variant="secondary"
                size="md"
                onClick={handleStopRecording}
                disabled={!isRecording}
              >
                Stop Recording
              </Button>
            </div>
          </div>
        </div>

        {/* Browser Info */}
        <div className="card">
          <div className="card-content">
            <h2 className="text-lg font-semibold mb-4">Browser Info</h2>
            <div className="space-y-2 text-sm">
              <div>
                <strong>User Agent:</strong>
                <div className="break-all text-xs text-gray-600 mt-1">
                  {navigator.userAgent}
                </div>
              </div>
              <div>
                <strong>MediaDevices:</strong>
                <span className="ml-2">{navigator.mediaDevices ? 'Available' : 'Not Available'}</span>
              </div>
              <div>
                <strong>getDisplayMedia:</strong>
                <span className="ml-2">
                  {'getDisplayMedia' in navigator.mediaDevices ? 'Available' : 'Not Available'}
                </span>
              </div>
              <div>
                <strong>MediaRecorder:</strong>
                <span className="ml-2">{window.MediaRecorder ? 'Available' : 'Not Available'}</span>
              </div>
              <div>
                <strong>Protocol:</strong>
                <span className="ml-2">{window.location.protocol}</span>
              </div>
              <div>
                <strong>Host:</strong>
                <span className="ml-2">{window.location.host}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Logs */}
      <div className="card mt-6">
        <div className="card-content">
          <h2 className="text-lg font-semibold mb-4">Debug Logs</h2>
          <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm max-h-64 overflow-y-auto">
            {logs.length === 0 ? (
              <div className="text-gray-500">No logs yet. Try starting a recording.</div>
            ) : (
              logs.map((log, index) => (
                <div key={index}>{log}</div>
              ))
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setLogs([])}
            className="mt-2"
          >
            Clear Logs
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RecordingDebug;
